import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { captureFbclid, ensureFbp, loadMetaPixel, trackMetaCapi } from '@/lib/tracking';

/**
 * Global Meta Pixel bootstrapper.
 * Loads the Pixel once, then fires a dual-delivery `PageView` (browser Pixel
 * + server CAPI, deduped by event_id) on every route change so Meta sees
 * each landing page (/, /pa, /spa, /pad, /flow/*) as its own view AND we
 * recover the ~30% of events iOS ATT + ad-blockers strip from browsers.
 * Also captures &fbclid= from the URL into the `_fbc` cookie so subsequent
 * server CAPI events inherit Meta click attribution.
 * Mount this ONCE inside the router (App.jsx), not per-page.
 */
export default function MetaTracker() {
  const location = useLocation();
  const pixelId = process.env.REACT_APP_META_PIXEL_ID || '';
  // Track whether the initial `loadMetaPixel` already fired a PageView so
  // we don't double-fire on first mount (loadMetaPixel calls trackMetaCapi
  // internally, then the location effect below would fire a second one).
  const bootedRef = useRef(false);

  // Boot the Pixel + capture fbclid + ensure fbp cookie on mount.
  useEffect(() => {
    if (!pixelId) return;
    captureFbclid();
    ensureFbp();
    loadMetaPixel(pixelId);   // Fires PageView + CAPI once, internally
    bootedRef.current = true;
  }, [pixelId]);

  // Fire a dual-delivery PageView on every SUBSEQUENT route change.
  useEffect(() => {
    if (!pixelId) return;
    if (!bootedRef.current) return;  // skip the very first fire (handled above)
    trackMetaCapi('PageView', { path: location.pathname + location.search });
  }, [location.key, pixelId]);

  return null;
}
